import React from 'react';
import ConceptTestForm from './useCaseForms/ConceptTestForm';
import ClaimsTestingForm from './useCaseForms/ClaimsTesting';
import SurveyAndFocusGroups from './useCaseForms/SurveyAndFocusGroups';
import PackagingReviewV2Form from './useCaseForms/PackagingReviewV2Form';
import PackagingVariantComparisonForm from './useCaseForms/PackagingVariantComparisonForm';
import AttributionForm from './useCaseForms/AttributionForm';
import ChannelStrategyForm from './useCaseForms/ABTestCreatives';
import ImageSurveyForm from './useCaseForms/ImageSurvey';
import TestUseCaseForm from './useCaseForms/TestUseCase';
import DrivingConceptTestForm from './useCaseForms/DrivingConceptTestForm';
import VitaminConceptTestForm from './useCaseForms/VitaminConceptTestForm';
import type { UseCaseType } from '../landing/LandingScreen';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface UseCaseFormRendererProps {
  useCaseType: UseCaseType;
  onSavePayload: (payload: Record<string, any>) => void;
  onBack: () => void;
  onFormDataChange?: (useCaseType: string, data: Record<string, any>) => void;
  initialFormData?: Record<string, any>;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  /**
   * Embedded in the marketplace (the audience is chosen upstream + the marketplace
   * provides its own review step). Drops the form's own fixed-viewport-width
   * wrapper so it aligns with the marketplace column, and tells the concept form
   * to skip its internal audience + review steps.
   */
  marketplaceMode?: boolean;
}

const UseCaseFormRenderer: React.FC<UseCaseFormRendererProps> = ({
  useCaseType,
  onSavePayload,
  onBack,
  onFormDataChange,
  initialFormData = {},
  selectedSegmentIds,
  personaFilters,
  marketplaceMode = false,
}) => {
  const commonProps = {
    onSubmit: () => {},
    selectedSegmentIds,
    personaFilters,
    onBack,
    onEditStep: () => {},
    initialFormData,
    onFormDataChange: onFormDataChange
      ? (data: Record<string, any>) => onFormDataChange(useCaseType as string, data)
      : undefined,
    onSavePayload,
  };

  const renderForm = () => {
    switch (useCaseType) {
      case 'concept-test':
        return <ConceptTestForm {...commonProps} />;
      case 'claims-testing':
        return <ClaimsTestingForm {...commonProps} />;
      case 'survey-and-focus-groups':
        return <SurveyAndFocusGroups {...commonProps} marketplaceMode={marketplaceMode} />;
      case 'packaging-review-v2':
        return <PackagingReviewV2Form {...commonProps} marketplaceMode={marketplaceMode} />;
      case 'packaging-variant-comparison':
        return <PackagingVariantComparisonForm {...commonProps} />;
      case 'attribution':
        return <AttributionForm {...commonProps} />;
      case 'channel-strategy':
        return <ChannelStrategyForm {...commonProps} />;
      case 'image-survey':
        return <ImageSurveyForm {...commonProps} />;
      case 'test-use-case':
        return <TestUseCaseForm {...commonProps} />;
      case 'driving-concept-test':
        return <DrivingConceptTestForm {...commonProps} />;
      case 'vitamin-concept-test':
        return <VitaminConceptTestForm {...commonProps} marketplaceMode={marketplaceMode} />;
      default:
        return (
          <div className="text-center py-12 text-gray-500">
            Form not available for this use case type.
          </div>
        );
    }
  };

  // Vitamin concept test owns the whole canvas — it is a self-contained
  // full-screen experience (its own progress + background) in BOTH the
  // enterprise app and the marketplace, so it skips the width wrapper.
  if (useCaseType === 'vitamin-concept-test') {
    return renderForm();
  }

  // In the marketplace the parent (MarketplaceShell column) already constrains the
  // width, so use full width and let it align with the page. The enterprise app
  // keeps the fixed 80vw column.
  return (
    <div className={marketplaceMode ? 'w-full' : 'mx-auto w-[80vw] max-w-4xl'}>{renderForm()}</div>
  );
};

export default UseCaseFormRenderer;
